// Google Play Billing via RevenueCat.
//
// SDK: @revenuecat/purchases-capacitor (the official RevenueCat plugin).
//
// Migrated from the previous Capgo purchases plugin, which could not be
// used here: its latest release still declares `peer @capacitor/core@^5.0.0`
// while this app runs @capacitor/core@8.5.0, so npm refused to install it.
// The old module worked around that by assembling its import specifier at
// runtime, to hide it from the bundler — which is why Android threw
// "Failed to resolve module specifier" the moment a user tapped Upgrade.
// Do not reintroduce that pattern: an import the bundler cannot see is an
// import that fails on device instead of at build time. The import below is
// a normal static one, so a missing package breaks CI, not a user.
//
// WHAT THIS MODULE IS AND ISN'T
//   It talks to the store and nothing else. It never decides what a user is
//   entitled to. Every purchase ends with the Play purchase token being sent
//   to the billing-verify Edge Function, which checks it with Google and
//   writes user_subscriptions / topup_credits with the service key.
//   SUPABASE IS THE SOURCE OF TRUTH FOR TIER — not RevenueCat's
//   customerInfo, and not anything this file returns. RevenueCat entitlements
//   are deliberately not consulted for gating; see src/lib/ads/adFree.ts.
//
// PLATFORM
//   Android only. On web and iOS every entry point returns
//   BILLING_UNAVAILABLE_MESSAGE rather than throwing, so the paywall can
//   render everywhere without a crash.
//
// DORMANT IN web_info MODE
//   While app_config.billing_mode is 'web_info' nothing in this file runs:
//   the paywall renders intent capture instead, and top-up UI is hidden.
//   Every export here is kept intact and working so flipping the mode back
//   to 'play' needs no code change — see src/lib/billing/appConfig.ts.
//
// SETUP
//   VITE_REVENUECAT_ANDROID_KEY — the RevenueCat *public* Android SDK key
//   (starts with "goog_"), from Project → API keys → the Google Play
//   "Public app-specific" key. Public by design: it identifies the app to
//   RevenueCat and grants nothing on its own, which is why it is safe in
//   the client bundle. Named per-platform because RevenueCat issues a
//   different public key for each store — an iOS build would read a
//   VITE_REVENUECAT_IOS_KEY, never this one.

import { Capacitor } from "@capacitor/core";
import { BILLING_UNAVAILABLE_FALLBACK } from "@/lib/featureFlags";
import {
  Purchases,
  PRODUCT_CATEGORY,
  type CustomerInfo,
  type PurchasesPackage,
  type PurchasesStoreProduct,
  type PurchasesStoreTransaction,
} from "@revenuecat/purchases-capacitor";

/** True on a device where a real purchase can be made. */
export const isPlayBillingAvailable = (): boolean =>
  Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";

/**
 * Shown wherever a purchase can't be started. Never surface a raw SDK or
 * module-resolution error to a user.
 *
 * Re-exported from featureFlags so there is exactly one such string in the
 * app. The previous wording named a specific place to buy, which web_info
 * mode must never do. The replacement states only that upgrading isn't
 * possible here; in web_info mode this path is unreachable anyway, because
 * the paywall's Upgrade -> Interested flow never opens a store.
 */
export const BILLING_UNAVAILABLE_MESSAGE = BILLING_UNAVAILABLE_FALLBACK;

// Product IDs — must match EXACTLY what you create in the Play Console AND
// what is registered as a Product in the RevenueCat dashboard.
//
// Subscriptions (Monetize → Subscriptions), monthly base plans only.
// Annual is deliberately absent: it returns in v1.1, and until then no
// annual price is offered anywhere in the UI.
export const PLAY_PRODUCTS = {
  pro: "com.sportsbio.player.pro",             // ₹199/month
  pro_plus: "com.sportsbio.player.pro_plus",   // ₹399/month
  unlimited: "com.sportsbio.player.unlimited", // ₹799/month
} as const;

export type PlayPlanKey = keyof typeof PLAY_PRODUCTS;

// In-app products (Monetize → In-app products), consumable.
export const PLAY_TOPUP_PRODUCT = "com.sportsbio.player.topup5"; // ₹99, +5 cards
/** Cards granted per top-up. The authoritative number is server-side in
 *  billing-verify's TOPUP_PRODUCTS map — this is for copy only. */
export const TOPUP_CARDS = 5;

export interface PlayBillingResult {
  success: boolean;
  purchaseToken?: string;
  orderId?: string;
  error?: string;
}

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

/** Purchases.configure() must run exactly once before any other call, and
 *  every other method rejects until it has. */
let configured = false;
let configuring: Promise<boolean> | null = null;

async function ensureConfigured(): Promise<boolean> {
  if (!isPlayBillingAvailable()) return false;
  if (configured) return true;
  if (configuring) return configuring;

  configuring = (async () => {
    // Android-only module, so only the Android key is ever read here.
    const apiKey = import.meta.env.VITE_REVENUECAT_ANDROID_KEY as string | undefined;
    if (!apiKey) {
      // eslint-disable-next-line no-console
      console.warn("[billing] VITE_REVENUECAT_ANDROID_KEY unset — Play Billing disabled");
      return false;
    }
    try {
      // appUserID null = anonymous. identifyUserForBilling() attaches the
      // SportsBio user id once auth resolves.
      await Purchases.configure({ apiKey, appUserID: null });
      configured = true;
      return true;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[billing] configure failed", err);
      return false;
    } finally {
      configuring = null;
    }
  })();

  return configuring;
}

/**
 * Initialise RevenueCat once at app start (Android only). Safe to call
 * unconditionally: no-ops on web/iOS or when the key is absent.
 */
export async function setupBilling(): Promise<void> {
  await ensureConfigured();
}

/** True when a real purchase can be started right now — native platform,
 *  key present, SDK configured. The paywall checks this before opening
 *  anything so an unavailable store reads as a sentence, not an error. */
export async function isBillingReady(): Promise<boolean> {
  return ensureConfigured();
}

/**
 * Attribute purchases to a specific SportsBio user. Call right after a
 * successful login. RevenueCat tracks entitlements per appUserID, so this
 * links the anonymous device to the account. Non-fatal on failure.
 */
export async function identifyUserForBilling(userId: string): Promise<void> {
  if (!(await ensureConfigured())) return;
  try {
    await Purchases.logIn({ appUserID: userId });
  } catch {
    /* non-fatal — purchases still work, just attributed anonymously */
  }
}

// ---------------------------------------------------------------------------
// Purchase
// ---------------------------------------------------------------------------

/**
 * The Play purchase token out of a completed purchase.
 *
 * PurchasesStoreTransaction.purchaseToken is documented "Android only" and
 * is exactly what billing-verify hands to the Play Developer API. It is
 * typed `string | null`, so callers must handle null rather than assume.
 */
function receiptFrom(tx: PurchasesStoreTransaction | undefined): {
  purchaseToken?: string;
  orderId?: string;
} {
  return {
    purchaseToken: tx?.purchaseToken ?? undefined,
    orderId: tx?.transactionIdentifier ?? undefined,
  };
}

/**
 * Find the Package whose underlying product matches `productId`, across
 * every configured offering.
 *
 * Matching on the PRODUCT identifier rather than the package identifier is
 * deliberate: package identifiers are free-text labels chosen in the
 * RevenueCat dashboard ("$rc_monthly", "pro", …) and this codebase has no
 * way to know them, whereas the product id is the same string the Play
 * Console uses and is already our source of truth (PLAY_PRODUCTS).
 *
 * Returns null when Offerings aren't configured or don't contain the
 * product — the caller then falls back to a direct product purchase.
 */
async function findPackageForProduct(productId: string): Promise<PurchasesPackage | null> {
  try {
    const offerings = await Purchases.getOfferings();
    const candidates = [
      ...(offerings.current ? [offerings.current] : []),
      ...Object.values(offerings.all ?? {}),
    ];
    for (const offering of candidates) {
      const match = offering.availablePackages?.find(
        (p) => p.product?.identifier === productId,
      );
      if (match) return match;
    }
    return null;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[billing] getOfferings failed, will try direct product", err);
    return null;
  }
}

/** Look the product up directly, for stores/dashboards without Offerings. */
async function findStoreProduct(
  productId: string,
  category: PRODUCT_CATEGORY,
): Promise<PurchasesStoreProduct | null> {
  try {
    const res = await Purchases.getProducts({
      productIdentifiers: [productId],
      type: category,
    });
    return res.products?.[0] ?? null;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[billing] getProducts failed", { productId, err });
    return null;
  }
}

/** True when a thrown purchase error represents the user backing out
 *  rather than a real failure. RevenueCat surfaces this as userCancelled. */
function isUserCancelled(err: unknown): boolean {
  const e = err as { userCancelled?: boolean; code?: string; message?: string } | null;
  return (
    e?.userCancelled === true ||
    e?.code === "PURCHASE_CANCELLED" ||
    /cancel/i.test(e?.message ?? "")
  );
}

function errorMessage(err: unknown, fallback: string): string {
  const e = err as { message?: string } | null;
  return e?.message || fallback;
}

/**
 * Run a purchase for one product id.
 *
 * Prefers purchasePackage() with a Package from getOfferings() — the flow
 * RevenueCat recommends, since it carries the offering context their
 * dashboard reports on. Falls back to purchaseStoreProduct() when the
 * product isn't in any offering, so purchases still work before the
 * dashboard's Offerings are set up.
 *
 * Resolves rather than rejects on failure so callers branch on `success`.
 */
async function purchaseProductId(
  productId: string,
  category: PRODUCT_CATEGORY,
): Promise<PlayBillingResult> {
  if (!(await ensureConfigured())) {
    return { success: false, error: BILLING_UNAVAILABLE_MESSAGE };
  }

  try {
    const aPackage = await findPackageForProduct(productId);

    const result = aPackage
      ? await Purchases.purchasePackage({ aPackage })
      : await (async () => {
          const product = await findStoreProduct(productId, category);
          if (!product) {
            throw new Error(
              "This plan isn't available from the store right now. Please try again later.",
            );
          }
          return Purchases.purchaseStoreProduct({ product });
        })();

    const { purchaseToken, orderId } = receiptFrom(result.transaction);
    if (!purchaseToken) {
      // Play always returns a token on Android. No token means we cannot
      // verify server-side, and an unverifiable purchase must not be
      // treated as success — the user would be charged with no entitlement.
      // eslint-disable-next-line no-console
      console.error("[billing] purchase completed with no purchaseToken", { productId });
      return {
        success: false,
        error:
          "Payment went through but we couldn't confirm it. Please contact support before retrying.",
      };
    }
    return { success: true, purchaseToken, orderId };
  } catch (err) {
    if (isUserCancelled(err)) {
      return { success: false, error: "Purchase cancelled" };
    }
    // eslint-disable-next-line no-console
    console.error("[billing] purchase failed", { productId, err });
    return { success: false, error: errorMessage(err, "Purchase failed") };
  }
}

/**
 * Launch the subscription purchase dialog for a product.
 *
 * The returned token is worthless on its own — it must be handed to the
 * billing-verify Edge Function, which checks it with Google and only then
 * moves the tier. Nothing client-side grants entitlement.
 */
export async function launchPlayBilling(productId: string): Promise<PlayBillingResult> {
  return purchaseProductId(productId, PRODUCT_CATEGORY.SUBSCRIPTION);
}

/**
 * Launch the purchase dialog for the CONSUMABLE top-up product. Separated
 * from launchPlayBilling so the call sites read clearly and so the product
 * category is right — Google returns nothing for a consumable queried as a
 * subscription.
 */
export async function launchTopupPurchase(): Promise<PlayBillingResult> {
  return purchaseProductId(PLAY_TOPUP_PRODUCT, PRODUCT_CATEGORY.NON_SUBSCRIPTION);
}

// ---------------------------------------------------------------------------
// Restore + metadata
// ---------------------------------------------------------------------------

/**
 * Restore previous purchases.
 *
 * IMPORTANT — what this can and cannot return.
 *   RevenueCat's restorePurchases() resolves with `customerInfo` only.
 *   customerInfo.nonSubscriptionTransactions carries a purchaseToken (so an
 *   unredeemed top-up is recoverable), but subscriptionsByProductIdentifier
 *   does NOT expose one. There is therefore no way to obtain a Play token
 *   for an active SUBSCRIPTION from a restore.
 *
 *   So the caller (usePlayerUpgrade.handleRestore) falls back to the token
 *   already stored on the user's own user_subscriptions row and re-verifies
 *   THAT with billing-verify. That is what detects an expiry or cancellation
 *   and drops the user back to free — which is what re-enables ads.
 */
export async function restorePurchases(): Promise<PlayBillingResult> {
  if (!(await ensureConfigured())) {
    return { success: false, error: BILLING_UNAVAILABLE_MESSAGE };
  }
  try {
    const { customerInfo } = await Purchases.restorePurchases();
    const tx = firstTokenBearingTransaction(customerInfo);
    if (!tx) {
      // Not an error: a subscription-only account legitimately has no
      // token-bearing transaction here.
      return { success: false, error: "No recoverable purchase found on this device." };
    }
    const { purchaseToken, orderId } = receiptFrom(tx);
    return purchaseToken
      ? { success: true, purchaseToken, orderId }
      : { success: false, error: "No recoverable purchase found on this device." };
  } catch (err) {
    return { success: false, error: errorMessage(err, "Restore failed") };
  }
}

function firstTokenBearingTransaction(
  info: CustomerInfo | null | undefined,
): PurchasesStoreTransaction | undefined {
  return (info?.nonSubscriptionTransactions ?? []).find((t) => !!t.purchaseToken);
}

/** Fetch store metadata (localized price/title) for products — used to show
 *  real store prices later. No-op-safe: returns [] off-device. */
export async function getPlayProducts(
  productIds: string[],
): Promise<PurchasesStoreProduct[]> {
  if (!(await ensureConfigured())) return [];
  try {
    const res = await Purchases.getProducts({ productIdentifiers: productIds });
    return res.products ?? [];
  } catch {
    return [];
  }
}
